import { useSidebarDndContext } from "@/contexts/SidebarDndProvider";
import { isPersonalProject } from "@/features/sidebar/projects/utils";
import { cn } from "@/lib/utils";
import { useDroppable } from "@dnd-kit/core";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import React from "react";

export function SortableWorkspaceItem({ workspace, children }) {
  const { draggedItemType, activeId, draggedItem } = useSidebarDndContext();
  const isPersonal = isPersonalProject(workspace);
  const disabled = isPersonal; // Personal Project cannot be dragged

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: workspace.id,
    disabled,
    data: {
      type: "project",
      item: workspace,
    },
  });

  // Make the project a droppable target for chats
  const { setNodeRef: setDroppableRef, isOver } = useDroppable({
    id: `workspace-drop-${workspace.id}`,
    data: {
      type: "workspace",
      workspaceId: workspace.id,
    },
  });

  // Get the dragged item's workspace ID
  const draggedItemWorkspaceId =
    draggedItem?.workspace_id || draggedItem?.workspaceId;

  // Check if we're dragging a chat over this workspace
  // Only show indicator for chats (not files), and only if the chat is not already in this workspace
  // Also check if dragging a file - files should never trigger workspace drop indicators
  const isDraggingChatOver =
    isOver &&
    draggedItemType === "chat" && // Only show for chats, not files
    draggedItemType !== "file" && // Explicitly exclude files
    activeId &&
    draggedItemWorkspaceId !== workspace.id; // Don't show if already in this workspace

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Combine refs for both sortable and droppable
  const combinedRef = (node) => {
    setNodeRef(node);
    setDroppableRef(node);
  };

  // Prevent drag when clicking on interactive elements
  const handlePointerDown = (e) => {
    if (
      e.target.closest("button") ||
      e.target.closest("a") ||
      e.target.closest("[role='button']") ||
      e.target.closest(".dropdown-menu-item") ||
      e.target.closest(".workspace-menu-trigger") ||
      e.target.closest("input") ||
      e.target.closest("textarea")
    ) {
      return;
    }
    if (!disabled && listeners?.onPointerDown) {
      listeners.onPointerDown(e);
    }
  };

  return (
    <div
      ref={combinedRef}
      style={style}
      {...attributes}
      tabIndex={-1}
      {...listeners}
      onPointerDown={handlePointerDown}
      className={cn(
        "relative",
        !disabled && "cursor-grab active:cursor-grabbing",
        isDragging && "opacity-50 z-50",
        isDraggingChatOver &&
          "ring-2 ring-purple-500/50 ring-offset-2 ring-offset-sidebar rounded-md"
      )}
    >
      {React.cloneElement(children, { isDraggingChatOver })}
    </div>
  );
}
